/**
 * SuperBrain — Reset Password Page
 * Consumes ?token=… from the email link, lets the user set a new password,
 * and auto-signs them in on success. Bumps token_version server-side so
 * every other live session is forcibly logged out.
 * © 2026 A-1 Affiliate Marketing LLC
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './AuthPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const MIN_LEN = 12;

const ResetPasswordPage = ({ onAuthenticated }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing reset token. Please use the link from your email.');
    }
  }, [token]);

  const strengthOk =
    newPassword.length >= MIN_LEN &&
    /[A-Za-z]/.test(newPassword) &&
    /[^A-Za-z\s]/.test(newPassword);

  const matches = newPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing reset token. Please use the link from your email.');
      return;
    }
    if (!strengthOk) {
      setError(
        `Password must be ${MIN_LEN}+ chars and include at least one letter and one number or symbol.`
      );
      return;
    }
    if (!matches) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      if (r?.data?.token && r?.data?.user) {
        localStorage.setItem('token', r.data.token);
        localStorage.setItem('user', JSON.stringify(r.data.user));
        setDone(true);
        // Brief celebration screen, then route into the app
        setTimeout(() => {
          if (onAuthenticated) onAuthenticated(r.data.user);
          navigate('/dashboard', { replace: true });
        }, 1400);
      } else {
        setError('Unexpected response from server. Please try signing in.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not reset password. The link may have expired — request a new one.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="reset-password-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>SuperBrain</h1>
          <p className="auth-subtitle">Choose a New Password</p>
        </div>

        {done ? (
          <div className="auth-form" data-testid="reset-success-panel">
            <div
              className="auth-info"
              data-testid="reset-success-message"
              style={{ textAlign: 'center', fontSize: '1.05rem' }}
            >
              ✓ Password reset. Signing you in…
            </div>
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            <p className="auth-help" style={{ marginBottom: '1.25rem' }}>
              Choose a strong new password. Minimum {MIN_LEN} characters with
              at least one letter and one number or symbol. All your other
              devices will be signed out for security.
            </p>

            <div className="form-group">
              <label>New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="At least 12 characters"
                data-testid="reset-new-password"
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Re-enter new password"
                data-testid="reset-confirm-password"
                autoComplete="new-password"
              />
            </div>

            <div className="checkbox-group" style={{ marginBottom: '1rem' }}>
              <input
                type="checkbox"
                id="reset-show-pw"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
                data-testid="reset-show-password-toggle"
              />
              <label htmlFor="reset-show-pw">Show password</label>
            </div>

            {newPassword.length > 0 && (
              <div
                className="auth-help"
                style={{
                  fontSize: '0.85rem',
                  color: strengthOk ? '#8aad6e' : '#e0a040',
                  marginBottom: '0.75rem',
                }}
                data-testid="reset-strength-indicator"
              >
                {strengthOk
                  ? '✓ Password meets requirements'
                  : `Needs ${MIN_LEN}+ chars, one letter, one number or symbol`}
              </div>
            )}

            {error && (
              <div className="auth-error" data-testid="reset-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="auth-submit"
              disabled={loading || !token || !strengthOk || !matches}
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting…' : 'Reset Password & Sign In'}
            </button>

            <button
              type="button"
              className="auth-link-btn"
              onClick={() => navigate('/')}
              data-testid="reset-back-to-login"
            >
              ← Back to sign in
            </button>
          </form>
        )}

        <div className="auth-footer">
          <p>✝ GOD BLESS EVERYONE ✝</p>
          <p className="auth-copyright">© 2026 A-1 Affiliate Marketing LLC</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
